import { useCallback } from 'react';
import * as ViewModel from '@/core/presenter/viewModel';
import clsx from 'clsx';
import { useStores } from '@/stores';
import { removeSignPrefix, removeSymbol, truncateString } from '@/utils';
import MemoArea from '@/components/atoms/MemoArea';
import { useNavigate } from 'react-router-dom';
import { RouterPath } from '@/routes/RouterPath';
import { tryFetchBalance } from '@/core/presenter/controller/HomeController';

interface IProps {
  tokenTransaction: ViewModel.IResponseActivity;
  onCancel: () => void;
}

const TokenTransactionPopup = ({ onCancel, tokenTransaction }: IProps) => {
  const { userStore } = useStores();
  const myTrxAddress = truncateString(userStore.user?.trxAddress ?? '');
  const navigate = useNavigate();

  const onClickTrxAddressCopyBtn = useCallback((text: string) => {
    navigator.clipboard.writeText(text);
  }, []);

  const onClickCopyBtn = useCallback(() => {
    navigator.clipboard.writeText(tokenTransaction.txID);
  }, [tokenTransaction.txID]);

  const onClickTrxExplorerBtn = useCallback(() => {
    window.open(`${import.meta.env.VITE_TRON_EXPLORER_URL}#/transaction/${tokenTransaction.txID}`, '_blank');
  }, [tokenTransaction.txID]);

  const onClickResendBtn = useCallback(async () => {
    if (userStore.user?.trxAddress) {
      const isSuccess = await tryFetchBalance(userStore.user.trxAddress);
      if (isSuccess) {
        navigate(RouterPath.HOME, {
          state: {
            viewType: ViewModel.HomeViewType.SEND,
            sendData: {
              address: tokenTransaction.type === '보내기' ? tokenTransaction.to : tokenTransaction.from,
            },
          },
        });
      }
    }

    onCancel();
  }, [navigate, tokenTransaction.type, tokenTransaction.to, tokenTransaction.from, onCancel]);

  return (
    <div className="modal" id="modal" style={{ display: 'flex' }}>
      <div className="modal-content">
        <div className="modal-body">
          <div className="inner">
            <div className="modal-header">
              <div className="close" onClick={onCancel}>
                <i className="bi bi-x-lg"></i>
              </div>
              <h3 className="title">{tokenTransaction.type} 상세내역</h3>
            </div>
            <div className="top detail-asset">
              <div className="left">
                <h3>상태</h3>
                <span
                  className={clsx('badge', {
                    'red-badge': tokenTransaction.status === ViewModel.TransactionStatus.FAIL,
                  })}
                >
                  {tokenTransaction.status}
                </span>
              </div>

              <div className="right">
                <span>
                  블록탐색기에서 보기 <i className="bi bi-search" onClick={onClickTrxExplorerBtn} />
                </span>
                <span>
                  트랜젝션 ID 복사 <i className="bi bi-copy" onClick={onClickCopyBtn} />
                </span>
              </div>
            </div>
            <div className="mid">
              <div className="sender">
                <h3>보낸 사람</h3>
              </div>
              <div className="receiver">
                <h3>받은 사람</h3>
              </div>
            </div>
            <MemoArea
              myTrxAddress={myTrxAddress}
              transaction={tokenTransaction}
              onClickResendBtn={onClickResendBtn}
              onClickTrxAddressCopyBtn={onClickTrxAddressCopyBtn}
            />
            <div className="bot asset-bot">
              <h3>트랜젝션</h3>
              <div className="container">
                <p>금액</p>
                <div className="right">
                  <p
                    className={clsx({
                      increase_color: tokenTransaction.type === '받기',
                      decrease_color: tokenTransaction.type === '보내기',
                    })}
                  >
                    {removeSymbol(removeSignPrefix(tokenTransaction.amount))}
                  </p>
                  <p>{tokenTransaction.coinType}</p>
                  <span>{tokenTransaction.krwAmount}</span>
                </div>
              </div>
              {tokenTransaction.type === '보내기' && (
                <div className="transaction-fee" style={{ display: 'flex' }}>
                  <p className="fee-label">수수료</p>
                  <p className="fee-value">{tokenTransaction.fee} TRX</p>
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="modal-footer">
          <button className="confirm-btn solid" onClick={onCancel}>
            확인
          </button>
        </div>
      </div>
    </div>
  );
};

export default TokenTransactionPopup;
